<template>
  <Modal
    cancelText=""
    :handleMainAction="handleCancel"
    :headerText="headerText"
    :mainActionAttention="canCancel ? true : false"
    :mainActionText="canCancel ? mainActionText : ''"
    :showClose="false"
    :visible="visible"
    @closed="handleModalClosed"
    @updateVisibility="handleUpdateVisibility"
  >
    <ProgressIndicator
      :completed="completed"
      :showCount="showCount"
      :total="total"
    />
  </Modal>
</template>
<script>
import { extend } from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import Modal from 'client/components/Modal/Modal.vue';
import ProgressIndicator from 'client/components/Modal/ModalComponents/ProgressIndicator.vue';

/**
 * The ProgressModal component displays the ProgressIndicator component in the
 * Modal component to display the progress of batch directory updates. e.g.
 * binding 10+ users to a system or user group.
 * @todo The ProgressModal will need to be able to be used with out a Vuex store
 * instead specifying the completed and total as props. This will allow it to be
 * used by legacy Backbone code.
 */
export default {
  name: 'ProgressModal',

  components: {
    Modal,
    ProgressIndicator,
  },

  props: {
    canCancel: {
      type: Boolean,
      default: false,
    },
    headerText: {
      type: String,
      default: 'Progress',
    },
    initialTotal: {
      type: Number,
      default: 0,
    },
    mainActionText: {
      type: String,
      default: 'cancel',
    },
    showCount: {
      type: Boolean,
      default: true,
    },
    storeModule: {
      type: String,
      default: 'ProgressModule',
    },
    useStoreModule: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      cancelled: false,
      completed: 0,
      total: this.initialTotal,
      hideProgressModalTimeoutId: null,
      visible: false,
    };
  },

  computed: {},

  watch: {
    counter: {
      deep: true,
      handler(newValue) {
        const { completed, total } = newValue;
        this.handleCounterChanged(completed, total);
      },
    },
  },

  beforeCreate() {
    const { props, propsData } = this.$options;
    const storeModule = propsData.storeModule || props.storeModule.default;

    extend(
      this.$options.computed,
      mapGetters(storeModule, {
        counter: 'getCounter',
      }),
    );

    extend(
      this.$options.methods,
      mapActions(storeModule, [
        'setCounter',
        'incrementCompleted',
      ]),
    );
  },

  mounted() {
    this.handleMounted();
  },

  methods: {
    clearHideProgressModalTimeout() {
      if (this.hideProgressModalTimeoutId) {
        window.clearTimeout(this.hideProgressModalTimeoutId);
        this.hideProgressModalTimeoutId = null;
      }
    },

    handleCancel() {
      this.cancelled = true;
      this.$emit('cancel');
    },

    handleCounterChanged(completed, total) {
      if (this.cancelled) {
        this.hideProgressModal(false);
        this.cancelled = false;
      } else if (total < 10) {
        this.hideProgressModal();
      } else if (completed >= total || total === 0) {
        this.updateProgressModal(completed, total);
        this.hideProgressModal();
      } else if (completed !== total && total > 0) {
        this.updateProgressModal(completed, total);
      }
    },

    /**
     * Emit a closed event when the Modal closes in case a parent component is
     * waiting for the ProgressModal to close before navigating to a new page
     * or otherwise removing the parent component.
     */
    handleModalClosed() {
      this.$emit('closed');
    },

    handleMounted() {
      if (this.initialTotal && this.useStoreModule) {
        this.setCounter({ completed: this.completed, total: this.initialTotal });
      }
    },

    handleUpdateVisibility() {
      // TODO: Ignore close events from the Modal until the operation in progress
      // has completed!
    },

    /**
     * When hiding the progress modal a timeout of 1s is set after which the
     * modal dialog is hidden. A timeout is used here because the aside panels
     * generally run batch operations in series, and rather than closing the
     * modal dialog and re-opening it for the next batch, we'd like to reuse it
     * to display the progress of the next batch. So if the next batch begins
     * within 1s of the previous batch ending then the modal will be reused.
     */
    hideProgressModal(useDelay = true) {
      this.clearHideProgressModalTimeout();

      this.hideProgressModalTimeoutId = window.setTimeout(() => {
        this.visible = false;
      }, useDelay ? 1000 : 0);
    },

    /**
     * When updating the progress modal create it if it doesn't yet exist. Also
     * clear the hide progress modal timeout so that if the next batch operation
     * begins within 1s of the last the progress modal will be reused.
     */
    updateProgressModal(completed, total) {
      this.clearHideProgressModalTimeout();

      this.visible = true;
      this.completed = completed;
      this.total = total;
    },
  },
};
</script>
